/**
 * The Generate-comment action and its root-mounted host (SPEC `Comment Generation` § The chat popup).
 *
 * The `SaveDialogProvider` pattern, for the same reason: a feed card, the post preview popup and a
 * `/watch` row all raise the same chat, so one host above all of them means one
 * `openCommentChat(postId)` and one dialog — stackable over the preview instead of trapped inside
 * whichever card raised it. Mounted in `AuthedLayout`, above every surface that renders a card.
 *
 * It also hosts its own `SettingsModal`. The setup state's "Open Settings" needs somewhere to go,
 * and screens own their settings modals today — a provider at the root has no host to borrow, and
 * two `Modal`s stack correctly (only the top one answers Escape). Configuring the backend there
 * refreshes `QK.ai` under the still-open chat, which flips it out of the setup state on its own.
 *
 * The readiness query is mounted here rather than in the dialog so the answer is already warm when
 * the popup opens — the `NotionSaveProvider` treatment.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { SettingsModal } from '../settings/SettingsModal';
import { useAiQuery } from '../settings/useAi';
import { CommentChatDialog } from './CommentChatDialog';
import './ai.css';

type CommentChatApi = {
  openCommentChat: (postId: number) => void;
};

const CommentChatContext = createContext<CommentChatApi | null>(null);

export function useCommentChat(): CommentChatApi {
  const api = useContext(CommentChatContext);
  if (api === null) throw new Error('useCommentChat must be used inside <CommentChatProvider>');
  return api;
}

export function CommentChatProvider({ children }: { children: ReactNode }) {
  const [postId, setPostId] = useState<number | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const openCommentChat = useCallback((id: number) => setPostId(id), []);
  const api = useMemo<CommentChatApi>(() => ({ openCommentChat }), [openCommentChat]);

  // Warm the readiness answer; the popup opens on a chat or a setup state with no extra round-trip.
  useAiQuery();

  return (
    <CommentChatContext.Provider value={api}>
      {children}
      {postId !== null ? (
        // Keyed by post: a chat opened on another post starts from its own blank state, never from
        // the previous post's draft or error row.
        <CommentChatDialog
          key={postId}
          postId={postId}
          onClose={() => setPostId(null)}
          onOpenSettings={() => setSettingsOpen(true)}
        />
      ) : null}
      {settingsOpen ? (
        <SettingsModal open initialTab="ai" onClose={() => setSettingsOpen(false)} />
      ) : null}
    </CommentChatContext.Provider>
  );
}

export default CommentChatProvider;
